/**
 * A `fetch` with a deadline, for the Supabase clients to use as their
 * transport.
 *
 * The failure this exists for isn't a slow answer, it's no answer: when the
 * project's database stops serving, PostgREST holds the connection open and
 * the request never settles. Every `await supabase.from(...)` in the app then
 * waits forever, which is how /admin ended up on a loading spinner with
 * nothing on screen to say why. Setting the deadline here means every query
 * gets one, instead of each call site having to remember.
 *
 * Three deliberate limits on how far it reaches:
 *
 *  1. Only `/rest/v1/` and `/auth/v1/` get a deadline. Storage uploads go
 *     through the same client and legitimately take minutes on poor mobile
 *     data — cutting those off at a fixed deadline would break photo and
 *     avatar uploads. Edge functions are left alone for the same reason.
 *  2. The clock stops when the response *headers* arrive, not when the body
 *     finishes. A slow or streamed body is not the hang we're guarding
 *     against, and aborting one would be a new bug.
 *  3. A caller-supplied signal still works. Supabase queries can pass one via
 *     `.abortSignal()`, and it's linked by hand rather than with
 *     `AbortSignal.any`, which isn't in every WebView this app runs in (older
 *     iOS) — the same reason src/redesign/faceRegistration.ts drives an
 *     AbortController off a timer.
 */

/** Generous: these calls are small, so anything near this is already wrong. */
export const SUPABASE_REQUEST_TIMEOUT_MS = 15_000;

/** Paths where a request that hasn't answered by the deadline is a fault. */
const DEADLINE_PATHS = ["/rest/v1/", "/auth/v1/"];

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  return input.url ?? "";
}

export function shouldApplyDeadline(input: RequestInfo | URL): boolean {
  const url = urlOf(input);
  return DEADLINE_PATHS.some((path) => url.includes(path));
}

export function fetchWithDeadline(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  if (!shouldApplyDeadline(input)) return fetch(input, init);

  const controller = new AbortController();
  const timer = setTimeout(
    () => controller.abort(),
    SUPABASE_REQUEST_TIMEOUT_MS,
  );

  // Keep honouring a signal the caller already passed.
  const callerSignal = init?.signal;
  let unlink: (() => void) | undefined;
  if (callerSignal) {
    if (callerSignal.aborted) {
      controller.abort();
    } else {
      const onAbort = () => controller.abort();
      callerSignal.addEventListener("abort", onAbort, { once: true });
      unlink = () => callerSignal.removeEventListener("abort", onAbort);
    }
  }

  // Cleared as soon as the headers land, so the body may stream for as long
  // as it likes without the deadline killing it mid-flight.
  return fetch(input, { ...init, signal: controller.signal }).finally(() => {
    clearTimeout(timer);
    unlink?.();
  });
}
